import { z } from 'zod';

// src/metadata-analytics/constants.ts
var METADATA_PATHS = {
  /** EAV row read + its `/timeseries`, `/highlights` and `/views/{id}` sub-routes. */
  analytics: "/metadata-analytics",
  metadata: "/metadata-analytics/metadata",
  timeseries: "/metadata-analytics/timeseries",
  highlights: "/metadata-analytics/highlights",
  analyticsViews: "/metadata-analytics/views",
  /** Explorer reads: the semantic field dictionary, tx stream, facet-value rail. */
  catalog: "/metadata-analytics/catalog",
  transactions: "/metadata-analytics/transactions",
  facetValues: "/metadata-analytics/facet-values",
  /** Build authored views into physical mv_* relations (write). */
  materialize: "/metadata-analytics/materialize",
  /** Config resources (each its own rewrite prefix). */
  facets: "/metadata-facets",
  views: "/metadata-views",
  paths: "/metadata-paths",
  policy: "/metadata-policy",
  dashboards: "/metadata-dashboards",
  resolve: "/metadata-resolve",
  /**
   * Protocol definitions and the per-scope `definition_patch` overlays that
   * retune them (ABIs, address book, confidence weights). One collection for
   * every rung of the ladder — a row's scope is part of its identity, so
   * authoring at a scope is a write to this same resource with a write scope.
   */
  protocols: "/metadata-protocols"
};
var ANALYTICS_SCOPES = ["merged", "system", "customer", "org", "tenant", "user"];
var DEFAULT_ANALYTICS_SCOPE = "merged";
var FILTER_OPS = [
  "eq",
  "neq",
  "in",
  "exists",
  "contains",
  // Range comparisons — for numeric / timestamp columns (e.g. `block_timestamp`).
  // See `ROW_COLUMN_FIELDS` on the back-end: ranges apply to physical columns.
  "gt",
  "gte",
  "lt",
  "lte"
];
var RANGE_FILTER_OPS = ["gt", "gte", "lt", "lte"];
var FILTER_OP_LABELS = {
  eq: "is",
  neq: "is not",
  in: "is any of",
  exists: "exists",
  contains: "contains",
  gt: "greater than",
  gte: "at least",
  lt: "less than",
  lte: "at most"
};
var INTERNAL_ANALYTICS_SCOPES = ["customer", "org"];
var SELF_SERVICE_ANALYTICS_SCOPES = ["merged", "system", "tenant", "user"];
var ANALYTICS_SCOPE_LABELS = {
  merged: "System + my tenant",
  system: "System defaults",
  customer: "Customer",
  org: "Organization",
  tenant: "My tenant",
  user: "My data"
};
var AGGREGATE_METRICS = [
  "count",
  "count_distinct_tx",
  "count_distinct_entity",
  "sum",
  "avg",
  "min",
  "max"
];
var NUMERIC_AGGREGATE_METRICS = ["sum", "avg", "min", "max"];
function isNumericMetric(metric) {
  return NUMERIC_AGGREGATE_METRICS.includes(metric ?? "");
}
var AGGREGATE_METRIC_LABELS = {
  count: "Facts",
  count_distinct_tx: "Transactions",
  count_distinct_entity: "Entities",
  // Named for what they do to the value rather than by their SQL function, so a
  // legend reads as a quantity ("Total") and not as an operation ("Sum").
  sum: "Total",
  avg: "Average",
  min: "Minimum",
  max: "Maximum"
};
var AGGREGATE_DIMENSIONS = [
  "canonical_name",
  "chain_id",
  "namespace",
  "source",
  "entity_type",
  "path_id"
];
var CCIP_PROTOCOL_ID = "chainlink_ccip";
var CCIP_FACETS = [
  "message_id",
  "source_chain",
  "dest_chain",
  "fee_token",
  "fee_amount",
  "token_amounts",
  "sender",
  "recipient"
];
var CCIP_VIEW_IDS = {
  message: "mv_ccip_message",
  byDestChain: "mv_ccip_by_dest_chain",
  byFeeToken: "mv_ccip_by_fee_token"
};
var TOKEN_LAUNCH_FACETS = [
  "launch_token",
  "launch_role",
  "launch_quote",
  "launch_pool",
  "token_deployer"
];
var TOKEN_LAUNCH_VIEW_IDS = {
  launch: "mv_token_launch",
  trading: "mv_token_trading"
};
var DEFAULT_ANALYTICS_LIMIT = 100;
var MIN_CONFIDENCE_BOUNDS = { min: 0, max: 1, step: 0.05 };
var CONFIDENCE_PRESETS = { lenient: 0.5, balanced: 0.7, cautious: 0.9 };

// src/metadata-analytics/time.ts
var TIME_FIELDS = ["block_timestamp", "ingested_at"];
var DEFAULT_TIME_FIELD = "block_timestamp";
var TIME_FIELD_LABELS = {
  block_timestamp: "Block time",
  ingested_at: "Ingest time"
};
var TIME_FIELD_HINTS = {
  block_timestamp: "When the transaction happened on-chain",
  ingested_at: "When we processed it \u2014 use this to find a run you just triggered"
};
var TIME_RANGE_PRESETS = [
  "1h",
  "6h",
  "24h",
  "7d",
  "30d",
  "90d"
];
var PRESET_SECONDS = {
  "1h": 3600,
  "6h": 21600,
  "24h": 86400,
  "7d": 604800,
  "30d": 2592e3,
  "90d": 7776e3
};
var PRESET_LABELS = {
  "1h": "Last hour",
  "6h": "Last 6 hours",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days"
};
function nowSeconds(nowMs) {
  return Math.floor((nowMs ?? Date.now()) / 1e3);
}
function resolveTimeRange(range, nowMs) {
  const now = nowSeconds(nowMs);
  if (range?.preset) {
    return { since: now - PRESET_SECONDS[range.preset], until: now };
  }
  const until = range?.until ?? now;
  const since = range?.since ?? until - PRESET_SECONDS["24h"];
  return { since, until };
}
var NICE_BUCKETS = [
  60,
  // 1m
  300,
  // 5m
  900,
  // 15m
  1800,
  // 30m
  3600,
  // 1h
  10800,
  // 3h
  21600,
  // 6h
  43200,
  // 12h
  86400,
  // 1d
  604800
  // 1w
];
function suggestBucketSeconds(window, targetBuckets = 60) {
  const span = Math.max(1, window.until - window.since);
  const ideal = span / Math.max(1, targetBuckets);
  for (const bucket of NICE_BUCKETS) {
    if (bucket >= ideal) return bucket;
  }
  return NICE_BUCKETS[NICE_BUCKETS.length - 1] ?? 604800;
}

// src/metadata-analytics/query.ts
function filterQuery(filters) {
  return {
    scope: filters.scope,
    canonical_names: filters.canonicalNames,
    value: filters.values,
    where: filters.where,
    tx_id: filters.txId,
    chain_id: filters.chainId,
    since: filters.since,
    until: filters.until,
    // Which clock `since`/`until` apply to (and the sort). Absent => block time.
    time_field: filters.timeField,
    // Only send is_highlight when set (tri-state): undefined => don't filter.
    is_highlight: filters.isHighlight,
    definition_scope: filters.definitionScope,
    min_confidence: filters.minConfidence,
    tenant_id: filters.tenantId
  };
}
function buildMetadataQuery(params = {}) {
  return {
    path: METADATA_PATHS.metadata,
    query: {
      ...filterQuery(params),
      limit: params.limit,
      cursor: params.cursor
    }
  };
}
function buildTimeseriesQuery(params) {
  return {
    path: METADATA_PATHS.timeseries,
    query: {
      ...filterQuery(params),
      bucket: params.bucketSeconds,
      metric: params.metric,
      group_by: params.groupBy,
      limit: params.limit
    }
  };
}
function buildViewReadQuery(viewId, params = {}) {
  const filters = params.filters ?? {};
  const filterPairs = Object.entries(filters).map(([column, value]) => `${column}:${value}`);
  return {
    path: `${METADATA_PATHS.analyticsViews}/${encodeURIComponent(viewId)}`,
    query: {
      scope: params.scope,
      filter: filterPairs.length ? filterPairs : void 0,
      limit: params.limit,
      tenant_id: params.tenantId
    }
  };
}
function buildHighlightsQuery(params = {}) {
  return {
    path: METADATA_PATHS.highlights,
    query: {
      scope: params.scope,
      protocol_id: params.protocolId,
      tx_id: params.txId,
      chain_id: params.chainId,
      since: params.since,
      until: params.until,
      min_confidence: params.minConfidence,
      limit: params.limit,
      tenant_id: params.tenantId
    }
  };
}
var configPaths = {
  facets: () => METADATA_PATHS.facets,
  facet: (name) => `${METADATA_PATHS.facets}/${encodeURIComponent(name)}`,
  views: () => METADATA_PATHS.views,
  view: (viewId) => `${METADATA_PATHS.views}/${encodeURIComponent(viewId)}`,
  viewsPlan: () => `${METADATA_PATHS.views}/plan`,
  paths: () => METADATA_PATHS.paths,
  path: (pathId) => `${METADATA_PATHS.paths}/${encodeURIComponent(pathId)}`,
  policy: () => METADATA_PATHS.policy,
  dashboards: () => METADATA_PATHS.dashboards,
  dashboard: (dashboardId) => `${METADATA_PATHS.dashboards}/${encodeURIComponent(dashboardId)}`,
  highlights: () => METADATA_PATHS.highlights,
  resolve: () => METADATA_PATHS.resolve,
  protocols: () => METADATA_PATHS.protocols,
  protocol: (protocolId) => `${METADATA_PATHS.protocols}/${encodeURIComponent(protocolId)}`
};
function scopeQuery(scope) {
  return scope ? { scope } : {};
}

// src/metadata-analytics/query-language.ts
var OP_SET = new Set(FILTER_OPS);
var RANGE_OPS = new Set(RANGE_FILTER_OPS);
var RANGE_PREFIXES = [
  [">=", "gte"],
  ["<=", "lte"],
  [">", "gt"],
  ["<", "lt"]
];
var FIELD_RE = /^[A-Za-z_][A-Za-z0-9_.]*$/;
var RELATIVE_TIME_RE = /^now(?:([+-])(\d+)([smhdw]))?$/i;
var RELATIVE_UNIT_SECONDS = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
  w: 604800
};
function tokenize(text) {
  const tokens = [];
  let buf = "";
  let quote = null;
  let depth = 0;
  for (const ch of text) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "(" || ch === "[") {
      depth += 1;
      buf += ch;
      continue;
    }
    if (ch === ")" || ch === "]") {
      depth = Math.max(0, depth - 1);
      buf += ch;
      continue;
    }
    if (/\s/.test(ch) && depth === 0) {
      if (buf) tokens.push(buf);
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf) tokens.push(buf);
  return tokens;
}
function unquote(value) {
  const v = value.trim();
  if (v.length >= 2) {
    const first = v[0];
    if ((first === '"' || first === "'") && v[v.length - 1] === first) {
      return v.slice(1, -1);
    }
  }
  return v;
}
function splitOr(inner) {
  return inner.split(/\s+OR\s+|,/i).map((v) => unquote(v)).filter((v) => v.length > 0);
}
function parseTimeValue(raw, nowMs) {
  const nowSec = Math.floor((nowMs ?? Date.now()) / 1e3);
  const t = raw.trim();
  if (!t) return null;
  if (t.toLowerCase() === "now") return nowSec;
  const rel = RELATIVE_TIME_RE.exec(t);
  if (rel) {
    if (!rel[1]) return nowSec;
    const sign = rel[1] === "-" ? -1 : 1;
    const mult = RELATIVE_UNIT_SECONDS[rel[3].toLowerCase()] ?? 1;
    return nowSec + sign * Number(rel[2]) * mult;
  }
  if (/^\d+$/.test(t)) {
    const n = Number(t);
    return n > 1e12 ? Math.floor(n / 1e3) : n;
  }
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1e3);
}
function coerceRangeValue(raw, nowMs) {
  const v = unquote(raw);
  if (/^\d+$/.test(v)) return v;
  const secs = parseTimeValue(v, nowMs);
  return secs != null ? String(secs) : v;
}
function parseExplicit(field, op, rawValue, negate, nowMs) {
  if (op === "exists") {
    if (negate) return { error: "negated exists is not supported" };
    return { filter: { field, op, values: [] } };
  }
  if (negate && op !== "eq" && op !== "neq") {
    return { error: `negation ('-') can't combine with '${op}'` };
  }
  const effectiveOp = negate ? op === "eq" ? "neq" : "eq" : op;
  if (op === "in") {
    const values = splitOr(rawValue);
    if (!values.length) return { error: "'in' needs at least one value" };
    return { filter: { field, op: "in", values } };
  }
  if (RANGE_OPS.has(op)) {
    const v2 = coerceRangeValue(rawValue, nowMs);
    if (!v2) return { error: `'${op}' needs a value` };
    return { filter: { field, op, values: [v2] } };
  }
  const v = unquote(rawValue);
  if (!v) return { error: `'${op}' needs a value` };
  return { filter: { field, op: effectiveOp, values: [v] } };
}
function parseToken(raw, nowMs) {
  let s = raw.trim();
  if (!s) return { error: "empty token" };
  let negate = false;
  if (s.startsWith("-")) {
    negate = true;
    s = s.slice(1);
  }
  const colon = s.indexOf(":");
  if (colon <= 0) return { error: "expected field:value" };
  const field = s.slice(0, colon).trim();
  if (!FIELD_RE.test(field)) return { error: `invalid field '${field}'` };
  const rhs = s.slice(colon + 1);
  if (rhs === "*") {
    if (negate) return { error: "negated exists is not supported" };
    return { filter: { field, op: "exists", values: [] } };
  }
  const nextColon = rhs.indexOf(":");
  if (nextColon !== -1) {
    const maybeOp = rhs.slice(0, nextColon).trim().toLowerCase();
    if (OP_SET.has(maybeOp)) {
      return parseExplicit(field, maybeOp, rhs.slice(nextColon + 1), negate, nowMs);
    }
  }
  if (rhs.startsWith("~")) {
    if (negate) return { error: "negated contains is not supported" };
    const v2 = unquote(rhs.slice(1));
    if (!v2) return { error: "'contains' needs a value" };
    return { filter: { field, op: "contains", values: [v2] } };
  }
  if (rhs.startsWith("(") && rhs.endsWith(")")) {
    if (negate) return { error: "negated group is not supported" };
    const values = splitOr(rhs.slice(1, -1));
    if (!values.length) return { error: "empty '( )' group" };
    return { filter: { field, op: "in", values } };
  }
  if (rhs.startsWith("[") && rhs.endsWith("]")) {
    if (negate) return { error: "negated range is not supported" };
    const parts = rhs.slice(1, -1).split(/\s+TO\s+/i);
    if (parts.length !== 2) return { error: "range must be [a TO b]" };
    const lo = coerceRangeValue(parts[0], nowMs);
    const hi = coerceRangeValue(parts[1], nowMs);
    return {
      filters: [
        { field, op: "gte", values: [lo] },
        { field, op: "lte", values: [hi] }
      ]
    };
  }
  for (const [prefix, op] of RANGE_PREFIXES) {
    if (rhs.startsWith(prefix)) {
      if (negate) return { error: `negated '${op}' is not supported` };
      const v2 = coerceRangeValue(rhs.slice(prefix.length), nowMs);
      if (!v2) return { error: `'${prefix}' needs a value` };
      return { filter: { field, op, values: [v2] } };
    }
  }
  const v = unquote(rhs);
  return { filter: { field, op: negate ? "neq" : "eq", values: [v] } };
}
function parseExplorerQuery(text, opts = {}) {
  const filters = [];
  const errors = [];
  for (const token of tokenize(text)) {
    const result = parseToken(token, opts.nowMs);
    if ("error" in result) errors.push({ token, reason: result.error });
    else if ("filters" in result) filters.push(...result.filters);
    else filters.push(result.filter);
  }
  return { filters, errors };
}
function needsQuote(value) {
  if (value === "") return true;
  if (/\s/.test(value)) return true;
  if (/["']/.test(value)) return true;
  if (/^[-*~([\]<>)]/.test(value)) return true;
  const colon = value.indexOf(":");
  if (colon !== -1 && OP_SET.has(value.slice(0, colon).toLowerCase())) return true;
  return false;
}
function quoteValue(value) {
  return needsQuote(value) ? JSON.stringify(value) : value;
}
var RANGE_SYMBOL = {
  gt: ">",
  gte: ">=",
  lt: "<",
  lte: "<="
};
function formatFilter(filter) {
  const { field, op } = filter;
  const first = filter.values[0] ?? "";
  switch (op) {
    case "exists":
      return `${field}:*`;
    case "neq":
      return `-${field}:${quoteValue(first)}`;
    case "in":
      return `${field}:(${filter.values.map(quoteValue).join(" OR ")})`;
    case "contains":
      return `${field}:~${quoteValue(first)}`;
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return `${field}:${RANGE_SYMBOL[op]}${quoteValue(first)}`;
    default:
      return `${field}:${quoteValue(first)}`;
  }
}
function formatExplorerQuery(filters) {
  return filters.map(formatFilter).join(" ");
}
var QueryGrammarError = class extends Error {
};
var KEYWORDS = /* @__PURE__ */ new Set(["AND", "OR", "NOT"]);
function consumeBalanced(text, i, open, close) {
  let depth = 0;
  let quote = null;
  for (; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === open) {
      depth += 1;
    } else if (ch === close) {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
  }
  throw new QueryGrammarError(`unbalanced '${open}'`);
}
function consumeQuote(text, i) {
  const quote = text[i];
  for (i += 1; i < text.length; i += 1) if (text[i] === quote) return i + 1;
  throw new QueryGrammarError("unterminated quote");
}
function tokenizeTree(text) {
  const toks = [];
  let buf = "";
  const flush = () => {
    if (!buf) return;
    const up = buf.toUpperCase();
    if (KEYWORDS.has(up)) toks.push({ kind: up, text: buf });
    else if (buf === "-") toks.push({ kind: "NOT", text: "-" });
    else toks.push({ kind: "TERM", text: buf });
    buf = "";
  };
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      flush();
      i += 1;
    } else if (ch === "(" && (buf === "" || buf === "-")) {
      if (buf === "-") {
        buf = "";
        toks.push({ kind: "NOT", text: "-" });
      }
      toks.push({ kind: "LPAREN", text: "(" });
      i += 1;
    } else if (ch === ")") {
      flush();
      toks.push({ kind: "RPAREN", text: ")" });
      i += 1;
    } else if (ch === "(") {
      const j = consumeBalanced(text, i, "(", ")");
      buf += text.slice(i, j);
      i = j;
    } else if (ch === "[") {
      const j = consumeBalanced(text, i, "[", "]");
      buf += text.slice(i, j);
      i = j;
    } else if (ch === '"' || ch === "'") {
      const j = consumeQuote(text, i);
      buf += text.slice(i, j);
      i = j;
    } else {
      buf += ch;
      i += 1;
    }
  }
  flush();
  return toks;
}
var leaf = (pred) => ({ type: "leaf", pred });
function combine(kind, nodes) {
  if (nodes.length === 1) return nodes[0];
  const flat = [];
  for (const n of nodes) {
    if (n.type === kind) flat.push(...n.children);
    else flat.push(n);
  }
  return { type: kind, children: flat };
}
function termNode(raw, nowMs) {
  let s = raw.trim();
  let negate = false;
  if (s.startsWith("-")) {
    negate = true;
    s = s.slice(1);
  }
  const colon = s.indexOf(":");
  if (colon === -1) {
    const word = unquote(s);
    if (!word) throw new QueryGrammarError(`expected field:value, got '${raw}'`);
    const node = { type: "freetext", word };
    return negate ? { type: "not", child: node } : node;
  }
  if (colon === 0) throw new QueryGrammarError(`expected field:value, got '${raw}'`);
  const res = parseToken(raw, nowMs);
  if ("error" in res) throw new QueryGrammarError(res.error);
  if ("filters" in res) return combine("and", res.filters.map(leaf));
  return leaf(res.filter);
}
var TreeParser = class {
  constructor(toks, nowMs) {
    this.toks = toks;
    this.nowMs = nowMs;
  }
  toks;
  nowMs;
  i = 0;
  peek() {
    return this.i < this.toks.length ? this.toks[this.i].kind : "";
  }
  parse() {
    const node = this.parseOr();
    if (this.i !== this.toks.length) throw new QueryGrammarError(`unexpected '${this.toks[this.i].text}'`);
    return node;
  }
  parseOr() {
    const nodes = [this.parseAnd()];
    while (this.peek() === "OR") {
      this.i += 1;
      nodes.push(this.parseAnd());
    }
    return combine("or", nodes);
  }
  parseAnd() {
    const nodes = [this.parseUnary()];
    for (; ; ) {
      const k = this.peek();
      if (k === "AND") {
        this.i += 1;
        nodes.push(this.parseUnary());
      } else if (k === "TERM" || k === "LPAREN" || k === "NOT") {
        nodes.push(this.parseUnary());
      } else break;
    }
    return combine("and", nodes);
  }
  parseUnary() {
    let negate = false;
    while (this.peek() === "NOT") {
      this.i += 1;
      negate = !negate;
    }
    const node = this.parsePrimary();
    return negate ? { type: "not", child: node } : node;
  }
  parsePrimary() {
    const tok = this.i < this.toks.length ? this.toks[this.i] : null;
    if (tok?.kind === "LPAREN") {
      this.i += 1;
      const node = this.parseOr();
      if (this.peek() !== "RPAREN") throw new QueryGrammarError("expected ')'");
      this.i += 1;
      return node;
    }
    if (tok?.kind === "TERM") {
      this.i += 1;
      return termNode(tok.text, this.nowMs);
    }
    throw new QueryGrammarError(tok ? `unexpected '${tok.text}'` : "unexpected end of query");
  }
};
function parseQuery(text, opts = {}) {
  if (!text || !text.trim()) return { node: null, errors: [] };
  try {
    const toks = tokenizeTree(text);
    if (!toks.length) return { node: null, errors: [] };
    return { node: new TreeParser(toks, opts.nowMs).parse(), errors: [] };
  } catch (err) {
    const reason = err instanceof QueryGrammarError ? err.message : "invalid query";
    return { node: null, errors: [{ token: text.trim(), reason }] };
  }
}
var nodePrec = { or: 1, and: 2, not: 3, leaf: 4, freetext: 4 };
function formatQuery(node) {
  if (!node) return "";
  const wrap = (child, min) => {
    const s = formatQuery(child);
    return nodePrec[child.type] < min ? `(${s})` : s;
  };
  switch (node.type) {
    case "leaf":
      return formatFilter(node.pred);
    case "freetext":
      return quoteValue(node.word);
    case "not":
      return node.child.type === "and" || node.child.type === "or" ? `-(${formatQuery(node.child)})` : `NOT ${wrap(node.child, 3)}`;
    case "and":
      return node.children.map((c) => wrap(c, 2)).join(" ");
    case "or":
      return node.children.map((c) => wrap(c, 1)).join(" OR ");
  }
}
function treeToFilters(node) {
  if (!node) return [];
  const out = [];
  const walk = (n) => {
    if (n.type === "leaf") {
      out.push(n.pred);
      return true;
    }
    if (n.type === "and") return n.children.every(walk);
    return false;
  };
  return walk(node) ? out : null;
}
function collectLeafFilters(node) {
  if (!node) return [];
  if (node.type === "leaf") return [node.pred];
  if (node.type === "not") return collectLeafFilters(node.child);
  if (node.type === "and" || node.type === "or") return node.children.flatMap(collectLeafFilters);
  return [];
}
function queryTextToExplorer(text, opts = {}) {
  const { node, errors } = parseQuery(text, opts);
  if (errors.length) return { filters: [], errors };
  if (node === null) return { filters: [], q: void 0, errors: [] };
  const chips = treeToFilters(node);
  if (chips !== null) return { filters: chips, q: void 0, errors: [] };
  return { filters: collectLeafFilters(node), q: formatQuery(node), errors: [] };
}
function appendAndClause(q, filter) {
  const clause = formatFilter(filter);
  const base = q.trim();
  if (!base) return clause;
  const { node } = parseQuery(base);
  const wrapped = node?.type === "or" ? `(${base})` : base;
  return `${wrapped} ${clause}`;
}

// src/metadata-analytics/explorer.ts
var EMPTY_EXPLORER_QUERY = { filters: [] };
function encodeFilter(filter) {
  if (filter.op === "exists") return `${filter.field}:exists`;
  const value = filter.op === "in" ? filter.values.join(",") : filter.values[0] ?? "";
  return `${filter.field}:${filter.op}:${value}`;
}
function decodeFilter(raw) {
  const first = raw.indexOf(":");
  const field = (first === -1 ? raw : raw.slice(0, first)).trim();
  if (!field) return null;
  if (first === -1) return null;
  const rest = raw.slice(first + 1);
  const second = rest.indexOf(":");
  const op = (second === -1 ? rest : rest.slice(0, second)).trim();
  if (!FILTER_OPS.includes(op)) return null;
  const value = second === -1 ? "" : rest.slice(second + 1);
  if (op === "exists") return { field, op, values: [] };
  if (op === "in") return { field, op, values: value.split(",").map((v) => v.trim()).filter(Boolean) };
  return { field, op, values: [value] };
}
function whereParams(filters) {
  const encoded = filters.map(encodeFilter);
  return encoded.length ? encoded : void 0;
}
function filterParams(query) {
  if (query.q && query.q.trim()) return { q: query.q.trim() };
  return { where: whereParams(query.filters) };
}
function ambientQuery(query) {
  const out = {
    scope: query.scope,
    chain_id: query.chainId,
    min_confidence: query.minConfidence,
    definition_scope: query.definitionScope,
    tenant_id: query.tenantId,
    time_field: query.timeField
  };
  if (query.timeRange) {
    const window = resolveTimeRange(query.timeRange);
    out.since = window.since;
    out.until = window.until;
  }
  return out;
}
function buildCatalogQuery(scope) {
  return { path: METADATA_PATHS.catalog, query: { scope } };
}
function buildTransactionsQuery(query, opts = {}) {
  return {
    path: METADATA_PATHS.transactions,
    query: {
      ...ambientQuery(query),
      ...filterParams(query),
      limit: opts.limit,
      cursor: opts.cursor
    }
  };
}
function buildFacetValuesQuery(field, query, opts = {}) {
  return {
    path: METADATA_PATHS.facetValues,
    query: {
      ...ambientQuery(query),
      field,
      ...filterParams(query),
      limit: opts.limit
    }
  };
}
function buildTransactionDetailQuery(txId, query = {}, opts = {}) {
  return {
    path: METADATA_PATHS.metadata,
    query: {
      tx_id: txId,
      scope: query.scope,
      min_confidence: query.minConfidence,
      tenant_id: query.tenantId,
      limit: opts.limit ?? 500
    }
  };
}
function explorerQueryToSearchParams(query) {
  const params = new URLSearchParams();
  if (query.scope) params.set("scope", query.scope);
  if (query.chainId) params.set("chain_id", query.chainId);
  if (query.minConfidence != null) params.set("min_confidence", String(query.minConfidence));
  if (query.definitionScope) params.set("definition_scope", query.definitionScope);
  if (query.tenantId) params.set("tenant_id", query.tenantId);
  if (query.timeField) params.set("time_field", query.timeField);
  if (query.timeRange?.preset) params.set("range", query.timeRange.preset);
  else if (query.timeRange) {
    if (query.timeRange.since != null) params.set("since", String(query.timeRange.since));
    if (query.timeRange.until != null) params.set("until", String(query.timeRange.until));
  }
  if (query.q && query.q.trim()) params.set("q", query.q.trim());
  else for (const filter of query.filters) params.append("where", encodeFilter(filter));
  return params;
}
function explorerQueryFromSearchParams(params) {
  const rawQ = params.get("q");
  const parsed = rawQ ? queryTextToExplorer(rawQ) : null;
  const filters = parsed ? parsed.filters : params.getAll("where").map(decodeFilter).filter((f) => f !== null);
  const q = parsed && !parsed.errors.length ? parsed.q : rawQ || void 0;
  const num = (key) => {
    const raw = params.get(key);
    if (raw == null || raw === "") return void 0;
    const parsed2 = Number(raw);
    return Number.isFinite(parsed2) ? parsed2 : void 0;
  };
  const preset = params.get("range");
  const since = num("since");
  const until = num("until");
  const timeRange = preset ? { preset } : since != null || until != null ? { since, until } : void 0;
  const rawTimeField = params.get("time_field");
  const timeField = TIME_FIELDS.includes(rawTimeField) ? rawTimeField : void 0;
  return {
    scope: params.get("scope") || void 0,
    chainId: params.get("chain_id") || void 0,
    minConfidence: num("min_confidence"),
    definitionScope: params.get("definition_scope") || void 0,
    tenantId: params.get("tenant_id") || void 0,
    timeRange,
    timeField,
    filters,
    q
  };
}
function withFilter(query, filter) {
  if (query.q && query.q.trim()) {
    const q = appendAndClause(query.q, filter);
    const parsed = queryTextToExplorer(q);
    if (parsed.errors.length) return { ...query, q };
    return { ...query, filters: parsed.filters, q: parsed.q };
  }
  const rest = query.filters.filter((f) => !(f.field === filter.field && f.op === filter.op));
  return { ...query, filters: [...rest, filter], q: void 0 };
}
function withoutFilter(query, index) {
  return { ...query, filters: query.filters.filter((_, i) => i !== index), q: void 0 };
}
function equalsFilter(field, value) {
  return { field, op: "eq", values: [value] };
}
function definitionOverrideScope(row) {
  const scope = row.definition_scope;
  if (!scope || scope === "system" || scope === "merged") return null;
  return ANALYTICS_SCOPES.includes(scope) ? scope : null;
}
function facetCreateBody(field, opts = {}) {
  return {
    name: field.name || field.canonical_name,
    canonical_name: field.canonical_name,
    canonical_type: field.type || "string",
    visibility: opts.visibility ?? "tenant",
    indexing: opts.indexing ?? "indexed",
    highlight: opts.highlight ?? false,
    description: field.label,
    enabled: true,
    ...field.protocol_id ? { namespaces: [field.protocol_id] } : {}
  };
}
function viewCreateBody(opts) {
  return {
    view_id: opts.viewId,
    facet_names: opts.facetNames,
    ...opts.scope ? { scope: opts.scope } : {},
    ...opts.source ? { source: opts.source } : {},
    ...opts.protocolId ? { protocol_id: opts.protocolId } : {},
    ...opts.groupBy ? { group_by: opts.groupBy } : {}
  };
}
function materializeRequest(viewId, scope) {
  return {
    path: METADATA_PATHS.materialize,
    query: { view_id: viewId, scope }
  };
}

// src/metadata-analytics/render.ts
function unwrapValue(value) {
  if (value === null || value === void 0) return value;
  if (typeof value === "object") {
    const o = value;
    const preferred = o.display ?? o.formatted ?? o.value ?? o.address ?? o.symbol ?? o.name;
    if (typeof preferred === "string" || typeof preferred === "number" || typeof preferred === "boolean") {
      return preferred;
    }
  }
  return value;
}
function shortenHex(value, lead = 6, tail = 4) {
  if (!value.startsWith("0x") || value.length <= lead + tail + 2) return value;
  return `${value.slice(0, lead + 2)}\u2026${value.slice(-tail)}`;
}
function renderFieldValue(value, kind) {
  const scalar = unwrapValue(value);
  if (scalar === null || scalar === void 0 || scalar === "") return "\u2014";
  const text = typeof scalar === "string" ? scalar : stringify(scalar);
  switch (kind) {
    case "address":
    case "message_id":
    case "tx_hash":
    case "hash":
      return shortenHex(text);
    default:
      return text;
  }
}
function stringify(value) {
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
function humanizeFieldName(name) {
  const spaced = name.replace(/[_.]+/g, " ").trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
var PANEL_KINDS = ["timeseries", "toplist", "kpi", "table", "distribution", "highlights"];
var PANEL_SOURCES = ["timeseries", "metadata", "view", "highlights"];
var TimeRangeSchema = z.object({
  preset: z.enum(TIME_RANGE_PRESETS).optional(),
  since: z.number().int().nonnegative().optional(),
  until: z.number().int().nonnegative().optional()
}).describe("A rolling preset or an absolute [since, until) unix-second window.");
var PanelFilterSchema = z.object({
  field: z.string().min(1),
  op: z.enum(FILTER_OPS),
  values: z.array(z.string()).default([])
});
var PanelQuerySchema = z.object({
  source: z.enum(PANEL_SOURCES),
  // Shared filters.
  canonicalNames: z.array(z.string()).optional(),
  values: z.array(z.string()).optional(),
  /**
   * Structured explorer chips (the `timeseries` / `metadata` sources honor them;
   * `view` / `highlights` ignore them). This is what an explorer view exports.
   */
  where: z.array(PanelFilterSchema).optional(),
  txId: z.string().optional(),
  chainId: z.string().optional(),
  isHighlight: z.boolean().optional(),
  minConfidence: z.number().gte(0).lte(1).optional(),
  /** Read scope; the viewer's scope control overrides it board-wide. */
  scope: z.enum(ANALYTICS_SCOPES).optional(),
  // timeseries.
  metric: z.enum(AGGREGATE_METRICS).optional(),
  groupBy: z.enum(AGGREGATE_DIMENSIONS).optional(),
  /** Fixed bucket width; when unset the panel auto-sizes to its window. */
  bucketSeconds: z.number().int().positive().optional(),
  // view.
  viewId: z.string().optional(),
  viewFilters: z.record(z.string(), z.string()).optional(),
  // highlights.
  protocolId: z.string().optional(),
  // paging.
  limit: z.number().int().positive().optional()
});
var PanelPositionSchema = z.object({
  x: z.number().int().nonnegative().default(0),
  y: z.number().int().nonnegative().default(0),
  w: z.number().int().positive().default(4),
  h: z.number().int().positive().default(3)
});
var DashboardPanelSchema = z.object({
  id: z.string().min(1),
  title: z.string().default(""),
  kind: z.enum(PANEL_KINDS),
  query: PanelQuerySchema,
  position: PanelPositionSchema.default({ x: 0, y: 0, w: 4, h: 3 }),
  /** Optional per-panel range; falls back to the dashboard range when unset. */
  timeRange: TimeRangeSchema.optional(),
  /** Optional prose the panel renders as a caption. */
  description: z.string().optional()
});
var TemplateVariableSchema = z.object({
  /** Referenced in panel queries as `$name` (bare word: letters/digits/_). */
  name: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "variable name must be a bare word"),
  /** The field the value filters on (`chain_id`, `protocol`, `dest_chain`, …). */
  field: z.string().min(1),
  /** Human label for the picker; falls back to the name. */
  label: z.string().optional(),
  /** Seed value; `*` (or unset) means "All" — the substitution drops the chip. */
  default: z.string().optional(),
  /** Allow selecting several values (compiles to an `in` chip). */
  multi: z.boolean().default(false)
});
var TEMPLATE_VARIABLE_ALL = "*";
var DashboardSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  /** Ladder rung the dashboard is authored at (system for a preset). */
  scope: z.enum(["system", "customer", "org", "tenant", "user"]).default("tenant"),
  timeRange: TimeRangeSchema.default({ preset: "24h" }),
  /** Board-level `$name` parameters (Datadog template variables). */
  templateVariables: z.array(TemplateVariableSchema).default([]),
  panels: z.array(DashboardPanelSchema).default([]),
  readonly: z.boolean().default(false),
  version: z.number().int().nonnegative().default(1)
});
function parseDashboard(value) {
  return DashboardSchema.parse(value);
}
function buildPanelQuery(panel, dashboardRange) {
  const window = resolveTimeRange(panel.timeRange ?? dashboardRange);
  const q = panel.query;
  const filters = {
    canonicalNames: q.canonicalNames,
    values: q.values,
    // Encode the structured chips to the repeated `where=` tokens the reads
    // parse (undefined when there are none, so the URL stays clean).
    where: whereParams(q.where ?? []),
    txId: q.txId,
    chainId: q.chainId,
    isHighlight: q.isHighlight,
    minConfidence: q.minConfidence,
    scope: q.scope,
    since: window.since,
    until: window.until
  };
  if (q.source === "timeseries") {
    return buildTimeseriesQuery({
      ...filters,
      bucketSeconds: q.bucketSeconds ?? suggestBucketSeconds(window),
      metric: q.metric,
      groupBy: q.groupBy,
      limit: q.limit
    });
  }
  if (q.source === "view") {
    if (!q.viewId) throw new Error("panel query source=view requires viewId");
    return buildViewReadQuery(q.viewId, { scope: q.scope, filters: q.viewFilters, limit: q.limit });
  }
  if (q.source === "highlights") {
    return buildHighlightsQuery({
      scope: q.scope,
      protocolId: q.protocolId,
      txId: q.txId,
      chainId: q.chainId,
      since: window.since,
      until: window.until,
      minConfidence: q.minConfidence,
      limit: q.limit
    });
  }
  return buildMetadataQuery({ ...filters, limit: q.limit });
}

// src/metadata-analytics/dashboard-edit.ts
var DASHBOARD_COLUMNS = 12;
var PANEL_DEFAULT_SIZE = {
  kpi: { w: 3, h: 2 },
  timeseries: { w: 6, h: 4 },
  toplist: { w: 6, h: 4 },
  distribution: { w: 6, h: 4 },
  table: { w: 12, h: 5 },
  highlights: { w: 12, h: 5 }
};
var EXPORTABLE_PANEL_KINDS = [
  "timeseries",
  "toplist",
  "kpi",
  "distribution",
  "table"
];
function shortId(prefix) {
  const rand = typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID().replace(/-/g, "").slice(0, 12) : Math.random().toString(36).slice(2, 14);
  return `${prefix}_${rand}`;
}
var genPanelId = () => shortId("panel");
var genDashboardId = () => shortId("dash");
function newDashboard(opts) {
  return parseDashboard({
    id: genDashboardId(),
    name: opts.name,
    description: opts.description,
    scope: opts.scope ?? "tenant",
    timeRange: { preset: "24h" },
    panels: [],
    readonly: false,
    version: 1
  });
}
function cloneDashboard(dashboard, name) {
  return parseDashboard({
    ...dashboard,
    id: genDashboardId(),
    name: name ?? `${dashboard.name} (copy)`,
    scope: dashboard.scope === "system" ? "tenant" : dashboard.scope,
    readonly: false,
    version: 1,
    // Fresh panel ids so the copy's panels never alias the preset's.
    panels: dashboard.panels.map((p) => ({ ...p, id: genPanelId() }))
  });
}
function reflowPanels(panels, columns = DASHBOARD_COLUMNS) {
  let x = 0;
  let rowTop = 0;
  let rowHeight = 0;
  return panels.map((panel) => {
    const w = Math.min(Math.max(panel.position.w, 1), columns);
    const h = Math.max(panel.position.h, 1);
    if (x + w > columns) {
      rowTop += rowHeight;
      x = 0;
      rowHeight = 0;
    }
    const placed = { ...panel, position: { ...panel.position, x, y: rowTop, w, h } };
    x += w;
    rowHeight = Math.max(rowHeight, h);
    return placed;
  });
}
function appendPanel(dashboard, panel) {
  const bottom = dashboard.panels.reduce(
    (max, p) => Math.max(max, p.position.y + p.position.h),
    0
  );
  const positioned = {
    ...panel,
    position: { ...panel.position, x: 0, y: bottom }
  };
  return { ...dashboard, panels: [...dashboard.panels, positioned] };
}
function updatePanel(dashboard, panelId, patch) {
  return {
    ...dashboard,
    panels: dashboard.panels.map((p) => p.id === panelId ? { ...p, ...patch } : p)
  };
}
function removePanel(dashboard, panelId) {
  return { ...dashboard, panels: dashboard.panels.filter((p) => p.id !== panelId) };
}
function duplicatePanel(dashboard, panelId) {
  const idx = dashboard.panels.findIndex((p) => p.id === panelId);
  if (idx === -1) return dashboard;
  const original = dashboard.panels[idx];
  if (!original) return dashboard;
  const copy = {
    ...original,
    id: genPanelId(),
    title: original.title ? `${original.title} (copy)` : original.title
  };
  const panels = [...dashboard.panels];
  panels.splice(idx + 1, 0, copy);
  return { ...dashboard, panels: reflowPanels(panels) };
}
function resizePanel(dashboard, panelId, size) {
  const panels = dashboard.panels.map(
    (p) => p.id === panelId ? {
      ...p,
      position: {
        ...p.position,
        w: size.w != null ? Math.min(Math.max(size.w, 1), DASHBOARD_COLUMNS) : p.position.w,
        h: size.h != null ? Math.max(size.h, 1) : p.position.h
      }
    } : p
  );
  return { ...dashboard, panels: reflowPanels(panels) };
}
function reorderPanels(dashboard, activeId, overId) {
  if (activeId === overId) return dashboard;
  const from = dashboard.panels.findIndex((p) => p.id === activeId);
  const to = dashboard.panels.findIndex((p) => p.id === overId);
  if (from === -1 || to === -1) return dashboard;
  const panels = [...dashboard.panels];
  const [moved] = panels.splice(from, 1);
  if (!moved) return dashboard;
  panels.splice(to, 0, moved);
  return { ...dashboard, panels: reflowPanels(panels) };
}
function sourceForKind(kind) {
  return kind === "timeseries" || kind === "kpi" ? "timeseries" : "metadata";
}
function panelFromExplorer(query, draft) {
  const where = query.filters.length ? query.filters.map((f) => ({ ...f })) : void 0;
  const source = sourceForKind(draft.kind);
  let panelQuery;
  if (source === "timeseries") {
    panelQuery = {
      source: "timeseries",
      metric: draft.metric ?? "count_distinct_tx",
      // A plain series / KPI need not split; a caller may still group a series.
      groupBy: draft.groupBy,
      chainId: query.chainId,
      where
    };
  } else if (draft.kind === "toplist" || draft.kind === "distribution") {
    panelQuery = {
      source: "metadata",
      canonicalNames: draft.field ? [draft.field] : void 0,
      chainId: query.chainId,
      where,
      limit: draft.limit ?? (draft.kind === "toplist" ? 200 : 500)
    };
  } else {
    panelQuery = {
      source: "metadata",
      chainId: query.chainId,
      where,
      limit: draft.limit ?? 50
    };
  }
  return {
    id: draft.id ?? genPanelId(),
    title: draft.title,
    kind: draft.kind,
    query: panelQuery,
    position: { x: 0, y: 0, ...PANEL_DEFAULT_SIZE[draft.kind] }
  };
}
var EXPLORER_ROUTE = "/analytics/explorer";
function explorerQueryFromPanel(panel, overrides = {}) {
  const q = panel.query;
  return {
    filters: (q.where ?? []).map((f) => ({ ...f })),
    chainId: q.chainId,
    scope: overrides.scope ?? q.scope,
    minConfidence: overrides.minConfidence ?? q.minConfidence
  };
}
function explorerHrefFromPanel(panel, overrides) {
  const search = explorerQueryToSearchParams(explorerQueryFromPanel(panel, overrides)).toString();
  return `${EXPLORER_ROUTE}${search ? `?${search}` : ""}`;
}

// src/metadata-analytics/controls.ts
function mapPanels(dashboard, fn) {
  return { ...dashboard, panels: dashboard.panels.map(fn) };
}
function withMinConfidence(dashboard, minConfidence) {
  return mapPanels(dashboard, (panel) => {
    if (panel.query.source === "view") return panel;
    const query = { ...panel.query };
    if (minConfidence === void 0) {
      delete query.minConfidence;
    } else {
      query.minConfidence = minConfidence;
    }
    return { ...panel, query };
  });
}
function withTimeRange(dashboard, timeRange) {
  return { ...dashboard, timeRange };
}
function withScope(dashboard, scope) {
  return mapPanels(dashboard, (panel) => {
    const query = { ...panel.query };
    if (scope === void 0) {
      delete query.scope;
    } else {
      query.scope = scope;
    }
    return { ...panel, query };
  });
}
function variableToken(value) {
  return value.startsWith("$") ? value.slice(1) : null;
}
var TITLE_TOKEN_RE = /\$([a-zA-Z][a-zA-Z0-9_]*)/g;
function withTemplateVariables(dashboard, selections) {
  const variables = dashboard.templateVariables ?? [];
  if (!variables.length) return dashboard;
  const known = new Map(variables.map((v) => [v.name, v]));
  const resolve = (name) => {
    const variable = known.get(name);
    if (!variable) return null;
    const raw = selections[name] ?? variable.default;
    if (raw == null) return null;
    const list = (Array.isArray(raw) ? raw : [raw]).filter(
      (v) => v != null && v !== "" && v !== TEMPLATE_VARIABLE_ALL
    );
    return list.length ? list : null;
  };
  const interpolate = (text) => {
    if (!text || !text.includes("$")) return text;
    return text.replace(TITLE_TOKEN_RE, (match, name) => {
      if (!known.has(name)) return match;
      const values = resolve(name);
      return values ? values.join(", ") : "all";
    });
  };
  return mapPanels(dashboard, (panel) => {
    const query = { ...panel.query };
    const title = interpolate(panel.title) ?? panel.title;
    const description = interpolate(panel.description);
    if (query.chainId) {
      const name = variableToken(query.chainId);
      if (name && known.has(name)) {
        const values = resolve(name);
        if (!values) delete query.chainId;
        else query.chainId = values[0];
      }
    }
    if (query.where?.length) {
      const rewritten = [];
      for (const chip of query.where) {
        const bound = chip.values.map(variableToken).find((n) => n && known.has(n));
        if (!bound) {
          rewritten.push(chip);
          continue;
        }
        const values = resolve(bound);
        if (!values) continue;
        rewritten.push({ ...chip, op: chip.op === "eq" && values.length > 1 ? "in" : chip.op, values });
      }
      query.where = rewritten.length ? rewritten : void 0;
    }
    return { ...panel, title, description, query };
  });
}

// src/metadata-analytics/token-launch.ts
var TOKEN_VAR = "$token";
var TOKEN_LAUNCH_DASHBOARD = parseDashboard({
  id: "preset_token_launch",
  name: "Token launch \u2014 Overview",
  description: "Deploy, liquidity, and trading for one launched token \u2014 and whether its developer sold.",
  scope: "system",
  readonly: true,
  // A launch's interesting window is its first hours, so the board opens on a
  // range that still contains the deploy rather than one that has scrolled past
  // it. Widen it and the early trades are still there; narrow it and they are
  // the only thing there.
  timeRange: { preset: "7d" },
  templateVariables: [
    {
      name: "token",
      field: "launch_token",
      label: "Token",
      multi: false
    }
  ],
  panels: [
    {
      id: "launch_kpi_volume",
      title: "Volume (USD)",
      description: "Summed over priced legs \u2014 empty until a price provider is wired.",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        metric: "sum",
        canonicalNames: ["value_usd"],
        where: [{ field: "launch_token", op: "eq", values: [TOKEN_VAR] }]
      }
    },
    {
      id: "launch_kpi_traders",
      title: "Traders",
      description: "Distinct accounts that traded the token.",
      kind: "kpi",
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        // count_distinct_entity over `origin`: the signer is the trader, and one
        // account swapping ten times is one trader, not ten.
        metric: "count_distinct_entity",
        canonicalNames: ["origin"],
        where: [{ field: "launch_token", op: "eq", values: [TOKEN_VAR] }]
      }
    },
    {
      id: "launch_ts_volume",
      title: "Trading volume",
      kind: "timeseries",
      position: { x: 6, y: 0, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "sum",
        canonicalNames: ["value_usd"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_role", op: "eq", values: ["swap"] }
        ]
      }
    },
    {
      id: "launch_ts_liquidity",
      title: "Liquidity removals",
      description: "A launch's liquidity leaving. The shape a rug makes.",
      kind: "timeseries",
      position: { x: 0, y: 2, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count_distinct_tx",
        canonicalNames: ["launch_role"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_role", op: "eq", values: ["liquidity_remove"] }
        ]
      }
    },
    {
      id: "launch_top_traders",
      title: "Top traders",
      description: "Who moved the most value. The first names here are the snipers.",
      kind: "toplist",
      position: { x: 0, y: 6, w: 6, h: 4 },
      query: {
        source: "metadata",
        canonicalNames: ["origin"],
        where: [
          { field: "launch_token", op: "eq", values: [TOKEN_VAR] },
          { field: "launch_role", op: "eq", values: ["swap"] }
        ],
        limit: 500
      }
    },
    {
      id: "launch_dist_role",
      title: "Activity by role",
      description: "Deploy, pool open, liquidity, trade \u2014 kept apart on purpose.",
      kind: "distribution",
      position: { x: 6, y: 6, w: 6, h: 4 },
      query: {
        source: "metadata",
        canonicalNames: ["launch_role"],
        where: [{ field: "launch_token", op: "eq", values: [TOKEN_VAR] }],
        limit: 500
      }
    },
    {
      id: "launch_table_launches",
      title: "Launches",
      description: "One row per launched token: what it trades against, where, and who deployed it.",
      kind: "table",
      position: { x: 0, y: 10, w: 12, h: 5 },
      query: { source: "view", viewId: TOKEN_LAUNCH_VIEW_IDS.launch, limit: 50 }
    },
    {
      id: "launch_table_trading",
      title: "Trading by role",
      kind: "table",
      position: { x: 0, y: 15, w: 12, h: 5 },
      query: { source: "view", viewId: TOKEN_LAUNCH_VIEW_IDS.trading, limit: 50 }
    }
  ]
});

// src/metadata-analytics/ccip.ts
var CCIP_OVERVIEW_DASHBOARD = parseDashboard({
  id: "preset_ccip_overview",
  name: "Chainlink CCIP \u2014 Overview",
  description: "Cross-chain message flow, chains, and fees for Chainlink CCIP.",
  scope: "system",
  readonly: true,
  timeRange: { preset: "7d" },
  panels: [
    {
      id: "ccip_kpi_messages",
      title: "Messages",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count_distinct_tx", canonicalNames: ["message_id"] }
    },
    {
      id: "ccip_kpi_facts",
      title: "Fee events",
      kind: "kpi",
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: { source: "timeseries", metric: "count", canonicalNames: ["fee_amount"] }
    },
    {
      id: "ccip_ts_volume",
      title: "Message volume",
      kind: "timeseries",
      position: { x: 6, y: 0, w: 6, h: 4 },
      query: { source: "timeseries", metric: "count_distinct_tx", canonicalNames: ["message_id"] }
    },
    {
      id: "ccip_top_source_chains",
      title: "Top source chains",
      kind: "toplist",
      position: { x: 0, y: 2, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count_distinct_tx",
        groupBy: "chain_id",
        canonicalNames: ["message_id"]
      }
    },
    {
      id: "ccip_dist_dest_chain",
      title: "Destination chains",
      kind: "distribution",
      position: { x: 0, y: 6, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["dest_chain"], limit: 500 }
    },
    {
      id: "ccip_dist_fee_token",
      title: "Fee token",
      kind: "distribution",
      position: { x: 6, y: 6, w: 6, h: 4 },
      query: { source: "metadata", canonicalNames: ["fee_token"], limit: 500 }
    },
    {
      id: "ccip_table_recent",
      title: "Recent messages",
      kind: "table",
      position: { x: 0, y: 10, w: 12, h: 5 },
      query: { source: "view", viewId: CCIP_VIEW_IDS.message, limit: 50 }
    },
    {
      id: "ccip_highlights",
      title: "Key attributes",
      description: "Render-ready highlights for recent CCIP messages.",
      kind: "highlights",
      position: { x: 0, y: 15, w: 12, h: 5 },
      query: { source: "highlights", protocolId: CCIP_PROTOCOL_ID, limit: 20 }
    }
  ]
});
var PRESET_DASHBOARDS = {
  [CCIP_OVERVIEW_DASHBOARD.id]: CCIP_OVERVIEW_DASHBOARD,
  [TOKEN_LAUNCH_DASHBOARD.id]: TOKEN_LAUNCH_DASHBOARD
};

// src/metadata-analytics/protocols.ts
var SYSTEM_ONLY_PATCH_FIELDS = [
  "protocol_id",
  "protocol_version",
  "category",
  "namespace",
  "handler_module",
  "decoder_module",
  "resolution"
];
function parseDefinitionPatch(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      ok: false,
      rejection: { kind: "unparseable", detail: err.message }
    };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, rejection: { kind: "not_an_object" } };
  }
  const patch = parsed;
  const keys = Object.keys(patch);
  if (keys.length === 0) return { ok: false, rejection: { kind: "empty" } };
  const denied = keys.filter(
    (k) => SYSTEM_ONLY_PATCH_FIELDS.includes(k)
  );
  if (denied.length) {
    return { ok: false, rejection: { kind: "system_only", fields: denied } };
  }
  return { ok: true, patch };
}
function describePatchRejection(rejection) {
  switch (rejection.kind) {
    case "unparseable":
      return `That isn't valid JSON: ${rejection.detail}`;
    case "not_an_object":
      return "An override is a map of definition fields, so it has to be a JSON object.";
    case "empty":
      return "An empty override is a delete \u2014 remove the override instead.";
    case "system_only":
      return `${rejection.fields.join(", ")} can only be set by the system definition, so an override can't change ${rejection.fields.length > 1 ? "them" : "it"}.`;
  }
}
function buildProtocolsQuery(scope) {
  return { path: configPaths.protocols(), query: scopeQuery(scope) };
}
function buildProtocolQuery(protocolId, scope) {
  return { path: configPaths.protocol(protocolId), query: scopeQuery(scope) };
}
function protocolOverrideRequest(protocolId, patch, scope) {
  return {
    path: configPaths.protocol(protocolId),
    query: scopeQuery(scope),
    body: { definition_patch: patch }
  };
}
function protocolOverrideDeleteRequest(protocolId, scope) {
  return { path: configPaths.protocol(protocolId), query: scopeQuery(scope) };
}
function protocolsPath() {
  return METADATA_PATHS.protocols;
}

export { AGGREGATE_DIMENSIONS, AGGREGATE_METRICS, AGGREGATE_METRIC_LABELS, ANALYTICS_SCOPES, ANALYTICS_SCOPE_LABELS, CCIP_FACETS, CCIP_OVERVIEW_DASHBOARD, CCIP_PROTOCOL_ID, CCIP_VIEW_IDS, CONFIDENCE_PRESETS, DASHBOARD_COLUMNS, DEFAULT_ANALYTICS_LIMIT, DEFAULT_ANALYTICS_SCOPE, DEFAULT_TIME_FIELD, DashboardPanelSchema, DashboardSchema, EMPTY_EXPLORER_QUERY, EXPLORER_ROUTE, EXPORTABLE_PANEL_KINDS, FILTER_OPS, FILTER_OP_LABELS, INTERNAL_ANALYTICS_SCOPES, METADATA_PATHS, MIN_CONFIDENCE_BOUNDS, NUMERIC_AGGREGATE_METRICS, PANEL_DEFAULT_SIZE, PANEL_KINDS, PANEL_SOURCES, PRESET_DASHBOARDS, PRESET_LABELS, PRESET_SECONDS, PanelFilterSchema, PanelPositionSchema, PanelQuerySchema, RANGE_FILTER_OPS, SELF_SERVICE_ANALYTICS_SCOPES, SYSTEM_ONLY_PATCH_FIELDS, TEMPLATE_VARIABLE_ALL, TIME_FIELDS, TIME_FIELD_HINTS, TIME_FIELD_LABELS, TIME_RANGE_PRESETS, TOKEN_LAUNCH_DASHBOARD, TOKEN_LAUNCH_FACETS, TOKEN_LAUNCH_VIEW_IDS, TemplateVariableSchema, TimeRangeSchema, appendAndClause, appendPanel, buildCatalogQuery, buildFacetValuesQuery, buildHighlightsQuery, buildMetadataQuery, buildPanelQuery, buildProtocolQuery, buildProtocolsQuery, buildTimeseriesQuery, buildTransactionDetailQuery, buildTransactionsQuery, buildViewReadQuery, cloneDashboard, collectLeafFilters, configPaths, decodeFilter, definitionOverrideScope, describePatchRejection, duplicatePanel, encodeFilter, equalsFilter, explorerHrefFromPanel, explorerQueryFromPanel, explorerQueryFromSearchParams, explorerQueryToSearchParams, facetCreateBody, filterParams, formatExplorerQuery, formatFilter, formatQuery, genDashboardId, genPanelId, humanizeFieldName, isNumericMetric, materializeRequest, newDashboard, panelFromExplorer, parseDashboard, parseDefinitionPatch, parseExplorerQuery, parseQuery, parseTimeValue, protocolOverrideDeleteRequest, protocolOverrideRequest, protocolsPath, queryTextToExplorer, reflowPanels, removePanel, renderFieldValue, reorderPanels, resizePanel, resolveTimeRange, scopeQuery, shortenHex, suggestBucketSeconds, treeToFilters, unwrapValue, updatePanel, viewCreateBody, whereParams, withFilter, withMinConfidence, withScope, withTemplateVariables, withTimeRange, withoutFilter };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map